import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Local storage key for help menu preference
 */
export const HELP_MENU_PREFERENCE_KEY = 'helpMenuShouldShow';

/**
 * Check if current route is a profile page
 * Excludes profile setup/wizard routes
 */
function isProfilePage(pathname: string): boolean {
  // Exclude setup and wizard routes
  if (pathname.includes('/setup/') || pathname.includes('/wizard/')) {
    return false;
  }
  // Matches /dashboard/orgs/:orgId/profiles/:profileId pattern
  return /\/dashboard\/orgs\/[^/]+\/profiles\/[^/]+/.test(pathname);
}

/**
 * Context for managing the help menu blade state
 */
interface HelpMenuContextType {
  /** Whether the help menu is open */
  isOpen: boolean;
  /** Whether the help menu should be shown (persisted preference) */
  shouldShowHelp: boolean;
  /** Open the help menu */
  open: () => void;
  /** Close the help menu */
  close: () => void;
  /** Toggle the help menu */
  toggle: () => void;
  /** Whether we're currently on a profile page */
  isProfilePage: boolean;
}

const HelpMenuContext = createContext<HelpMenuContextType | null>(null);

/**
 * Hook to access the help menu context
 */
export function useHelpMenu() {
  const context = useContext(HelpMenuContext);
  if (!context) {
    // eslint-disable-next-line no-restricted-syntax
    throw new Error('useHelpMenu must be used within a HelpMenuProvider');
  }
  return context;
}

/**
 * Provider for the help menu context
 */
export function HelpMenuProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  // Check if we're on a profile page
  const isProfile = isProfilePage(location.pathname);

  // Load preference from localStorage (defaults to true)
  const [shouldShowHelp, setShouldShowHelp] = useState<boolean>(() => {
    const stored = localStorage.getItem(HELP_MENU_PREFERENCE_KEY);
    return stored !== null ? stored === 'true' : true;
  });

  // Auto-open help menu when on profile page if preference is true
  useEffect(() => {
    // Re-read preference from localStorage when navigating to a profile page
    // This allows the preference to be updated (e.g., when creating a new profile)
    if (isProfile) {
      const stored = localStorage.getItem(HELP_MENU_PREFERENCE_KEY);
      const shouldShow = stored !== null ? stored === 'true' : true;
      setShouldShowHelp(shouldShow);
      
      if (shouldShow) {
        setIsOpen(true);
      }
    } else {
      // Close when navigating away from profile pages
      setIsOpen(false);
    }
  }, [isProfile]);

  const open = useCallback(() => {
    setIsOpen(true);
    // If opening on a profile page, save preference to show it
    if (isProfile) {
      setShouldShowHelp(true);
      localStorage.setItem(HELP_MENU_PREFERENCE_KEY, 'true');
    }
  }, [isProfile]);

  const close = useCallback(() => {
    setIsOpen(false);
    // If closing on a profile page, save preference to not show it
    if (isProfile) {
      setShouldShowHelp(false);
      localStorage.setItem(HELP_MENU_PREFERENCE_KEY, 'false');
    }
  }, [isProfile]);

  const toggle = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, close, open]);

  const contextValue = useMemo(
    () => ({
      isOpen,
      shouldShowHelp,
      open,
      close,
      toggle,
      isProfilePage: isProfile,
    }),
    [isOpen, shouldShowHelp, open, close, toggle, isProfile]
  );

  return (
    <HelpMenuContext.Provider value={contextValue}>
      {children}
    </HelpMenuContext.Provider>
  );
}

